import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "../../supabase";
import { useApi } from "../../contexts/ApiContext";
import { useFeatures } from "../../contexts/FeatureContext";
import { useAuth } from "../../contexts/AuthContext";
import "../../styles/crud.css";
import "./SettingsPage.css";

// ── Toggle component ──────────────────────────────────────────────────────────
function Toggle({ enabled, onChange }) {
  return (
    <button
      className={`feature-toggle${enabled ? " on" : ""}`}
      onClick={() => onChange(!enabled)}
      aria-label="Toggle"
    >
      <span className="feature-toggle-thumb" />
    </button>
  );
}

// ── Features section (shared by guest and auth) ───────────────────────────────
function FeaturesSection() {
  const { flags, setFlag } = useFeatures();

  return (
    <div className="settings-card">
      <h2 className="settings-card-title">Features</h2>
      <div className="feature-row">
        <div className="feature-info">
          <span className="feature-name">Ratings</span>
          <span className="feature-desc">
            Enable Elo-based player ratings. Tracks rating changes after every
            match and allows seeded bracket generation.
          </span>
        </div>
        <Toggle
          enabled={flags.ratingsEnabled}
          onChange={(val) => setFlag("ratingsEnabled", val)}
        />
      </div>

      <div className="feature-divider" />

      <div className="feature-row">
        <div className="feature-info">
          <span className="feature-name">Scoring</span>
          <span className="feature-desc">
            Record a score for each match. When recording a winner you'll be
            asked to enter the winner's and loser's score, shown on the bracket.
          </span>
        </div>
        <Toggle
          enabled={flags.scoringEnabled}
          onChange={(val) => setFlag("scoringEnabled", val)}
        />
      </div>
    </div>
  );
}

// ── Guest view ────────────────────────────────────────────────────────────────
function GuestSettings({ onExitGuest }) {
  return (
    <div className="crud-page">
      <div className="crud-header">
        <h1 className="crud-title">Settings</h1>
        <p className="crud-sub">You are browsing as a guest.</p>
      </div>

      <FeaturesSection />

      <div className="settings-card">
        <h2 className="settings-card-title">Guest Mode</h2>
        <p className="settings-danger-desc">
          You're currently in guest mode. Your players, tournaments, and match
          results are stored in memory only and will be lost when you close this
          tab.
        </p>
        <p className="settings-danger-desc">
          Create a free account to save your data permanently and access it from
          any device.
        </p>
        <button className="crud-btn-primary" onClick={onExitGuest}>
          Sign Up or Sign In
        </button>
      </div>
    </div>
  );
}

// ── Authenticated view ────────────────────────────────────────────────────────
function AuthSettings() {
  const { playersApi, tournamentsApi, matchResultsApi } = useApi();
  const queryClient = useQueryClient();
  const [user, setUser] = useState(null);
  const [username, setUsername] = useState("");
  const [newUsername, setNewUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirm] = useState("");
  const [usernameMsg, setUsernameMsg] = useState("");
  const [passwordMsg, setPasswordMsg] = useState("");
  const [deleteMsg, setDeleteMsg] = useState("");
  const [loadingUser, setLoadingUser] = useState(false);
  const [loadingPass, setLoadingPass] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      setUser(user);
      setUsername(user?.user_metadata?.username || "");
    });
  }, []);

  async function handleUpdateUsername() {
    if (!newUsername.trim())
      return setUsernameMsg("Please enter a new username.");
    setLoadingUser(true);
    setUsernameMsg("");
    try {
      const { error } = await supabase.auth.updateUser({
        data: { username: newUsername.trim() },
      });
      if (error) throw error;
      setUsername(newUsername.trim());
      setNewUsername("");
      setUsernameMsg("✓ Username updated successfully.");
    } catch (e) {
      setUsernameMsg(e.message);
    } finally {
      setLoadingUser(false);
    }
  }

  async function handleUpdatePassword() {
    if (!newPassword) return setPasswordMsg("Please enter a new password.");
    if (newPassword !== confirmPassword)
      return setPasswordMsg("Passwords do not match.");
    if (newPassword.length < 6)
      return setPasswordMsg("Password must be at least 6 characters.");
    setLoadingPass(true);
    setPasswordMsg("");
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });
      if (error) throw error;
      setNewPassword("");
      setConfirm("");
      setPasswordMsg("✓ Password updated successfully.");
    } catch (e) {
      setPasswordMsg(e.message);
    } finally {
      setLoadingPass(false);
    }
  }

  async function handleDeleteData() {
    if (!confirmDelete) {
      setConfirmDelete(true);
      setDeleteMsg(
        "Are you sure? Click Delete again to confirm. This cannot be undone.",
      );
      return;
    }
    setLoadingDelete(true);
    setDeleteMsg("");
    try {
      const [players, tournaments] = await Promise.all([
        playersApi.getAll(),
        tournamentsApi.getAll(),
      ]);
      await Promise.all([
        ...players.map((p) => playersApi.delete(p.id)),
        ...tournaments.map((t) => tournamentsApi.delete(t.id)),
        matchResultsApi.deleteAll(),
      ]);
      queryClient.invalidateQueries({ queryKey: ["players"] });
      queryClient.invalidateQueries({ queryKey: ["tournaments"] });
      queryClient.invalidateQueries({ queryKey: ["matchResults"] });
      setConfirmDelete(false);
      setDeleteMsg("✓ All your data has been deleted.");
    } catch (e) {
      setDeleteMsg(e.message);
    } finally {
      setLoadingDelete(false);
    }
  }

  return (
    <div className="crud-page">
      <div className="crud-header">
        <h1 className="crud-title">Settings</h1>
        <p className="crud-sub">Manage your account and data.</p>
      </div>

      {/* ── Profile summary ── */}
      <div className="settings-card">
        <h2 className="settings-card-title">Account</h2>
        <div className="settings-summary">
          <div className="settings-avatar">
            {username.charAt(0).toUpperCase() || "?"}
          </div>
          <div className="settings-info">
            <div className="settings-info-name">{username || "—"}</div>
            <div className="settings-info-email">{user?.email || "—"}</div>
          </div>
        </div>
      </div>

      {/* ── Update username ── */}
      <div className="settings-card">
        <h2 className="settings-card-title">Update Username</h2>
        <div className="crud-form-row">
          <input
            className="tn-input"
            placeholder="New username"
            value={newUsername}
            onChange={(e) => {
              setNewUsername(e.target.value);
              setUsernameMsg("");
            }}
            onKeyDown={(e) => e.key === "Enter" && handleUpdateUsername()}
          />
          <button
            className="crud-btn-primary"
            onClick={handleUpdateUsername}
            disabled={loadingUser}
          >
            {loadingUser ? "Saving..." : "Update"}
          </button>
        </div>
        {usernameMsg && (
          <p
            className={`settings-msg ${usernameMsg.startsWith("✓") ? "settings-msg-success" : "settings-msg-error"}`}
          >
            {usernameMsg}
          </p>
        )}
      </div>

      {/* ── Update password ── */}
      <div className="settings-card">
        <h2 className="settings-card-title">Update Password</h2>
        <div className="settings-stack">
          <input
            className="tn-input"
            type="password"
            placeholder="New password"
            value={newPassword}
            onChange={(e) => {
              setNewPassword(e.target.value);
              setPasswordMsg("");
            }}
          />
          <input
            className="tn-input"
            type="password"
            placeholder="Confirm new password"
            value={confirmPassword}
            onChange={(e) => {
              setConfirm(e.target.value);
              setPasswordMsg("");
            }}
          />
          <button
            className="crud-btn-primary settings-btn-full"
            onClick={handleUpdatePassword}
            disabled={loadingPass}
          >
            {loadingPass ? "Saving..." : "Update Password"}
          </button>
        </div>
        {passwordMsg && (
          <p
            className={`settings-msg ${passwordMsg.startsWith("✓") ? "settings-msg-success" : "settings-msg-error"}`}
          >
            {passwordMsg}
          </p>
        )}
      </div>

      {/* ── Features ── */}
      <FeaturesSection />

      {/* ── Danger zone ── */}
      <div className="settings-card settings-card-danger">
        <h2 className="settings-card-title settings-card-title-danger">
          Danger Zone
        </h2>
        <p className="settings-danger-desc">
          Permanently delete all your players, tournaments, and match results.
          This cannot be undone.
        </p>
        <button
          className="crud-btn-danger settings-btn-delete"
          onClick={handleDeleteData}
          disabled={loadingDelete}
        >
          {loadingDelete
            ? "Deleting..."
            : confirmDelete
              ? "Confirm Delete"
              : "Delete All My Data"}
        </button>
        {deleteMsg && (
          <p
            className={`settings-msg ${deleteMsg.startsWith("✓") ? "settings-msg-success" : "settings-msg-warn"}`}
          >
            {deleteMsg}
          </p>
        )}
      </div>
    </div>
  );
}

function SettingsPage() {
  const { isGuest, exitGuest } = useAuth();
  const navigate = useNavigate();
  if (isGuest)
    return (
      <GuestSettings
        onExitGuest={() => {
          exitGuest();
          navigate("/sign-in");
        }}
      />
    );
  return <AuthSettings />;
}

export default SettingsPage;
